import { useEffect, useRef } from "react";
import type { ChapterId } from "./types";
import { useTour, type ChapterHooks } from "./TourProvider";

/** Register per-chapter hooks for the page's lifetime: `onStart` (read through
 *  a ref, so a fresh object per render never re-registers) and, for pages that
 *  host hands-on steps, the `context` their predicates read. The context is
 *  forwarded from an effect keyed on its two identities, so a page that
 *  passes `{ annotations: snapshot.annotations, document }` every render only
 *  reaches the provider when the snapshot actually changes. */
export function useTourChapterHook(chapterId: ChapterId, hooks: ChapterHooks): void {
  const { registerChapterHook, setChapterContext } = useTour();
  const latest = useRef(hooks);
  latest.current = hooks;
  useEffect(
    () => registerChapterHook(chapterId, { onStart: () => latest.current.onStart?.() }),
    [chapterId, registerChapterHook],
  );
  const annotations = hooks.context?.annotations ?? null;
  const doc = hooks.context?.document ?? null;
  useEffect(() => {
    if (doc === null) return;
    setChapterContext(chapterId, { annotations, document: doc });
  }, [chapterId, annotations, doc, setChapterContext]);
}
